import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { customerApi } from "@/lib/api";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const RELATIONS = ["Spouse", "Child", "Parent", "Sibling", "Grandparent", "Other"];

const EMPTY = { fullName: "", relationship: "", dateOfBirth: "", gender: "", bloodGroup: "", phone: "" };

export default function DependentDialog({ open, onOpenChange, userId, dependent, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!dependent;

  useEffect(() => {
    if (open) {
      if (dependent) {
        setForm({
          fullName: dependent.fullName || dependent.name || "",
          relationship: dependent.relationship || dependent.relation || "",
          dateOfBirth: dependent.dateOfBirth ? formatDob(dependent.dateOfBirth) : "",
          gender: dependent.gender || "",
          bloodGroup: dependent.bloodGroup || "",
          phone: dependent.phone || "",
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, dependent]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.fullName.trim()) return toast.error("Name is required");
    if (!form.relationship) return toast.error("Please select a relationship");
    setSaving(true);
    try {
      const payload = {
        userId,
        fullName: form.fullName.trim(),
        relationship: form.relationship,
        dateOfBirth: form.dateOfBirth || undefined,
        gender: form.gender || undefined,
        bloodGroup: form.bloodGroup || undefined,
        phone: form.phone || undefined,
      };
      if (isEdit) await customerApi.updateDependent(dependent._id || dependent.id, payload);
      else await customerApi.addDependent(payload);
      toast.success(isEdit ? "Member updated" : "Family member added");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save member");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit family member" : "Add family member"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Full name *</Label>
            <Input value={form.fullName} onChange={(e) => set("fullName", e.target.value)} className="mt-1.5 rounded-2xl h-11" data-testid="dep-name" />
          </div>
          <div>
            <Label>Relationship *</Label>
            <Select value={form.relationship} onValueChange={(v) => set("relationship", v)}>
              <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="dep-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date of birth (DD/MM/YYYY)</Label>
            <Input value={form.dateOfBirth} onChange={(e) => set("dateOfBirth", e.target.value)} placeholder="15/08/2010" className="mt-1.5 rounded-2xl h-11" data-testid="dep-dob" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="dep-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Blood group</Label>
              <Select value={form.bloodGroup} onValueChange={(v) => set("bloodGroup", v)}>
                <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="dep-blood"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {BLOOD_GROUPS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, ""))} maxLength={10} className="mt-1.5 rounded-2xl h-11" data-testid="dep-phone" />
          </div>
        </div>
        <Button className="w-full rounded-2xl h-11 mt-2" onClick={save} disabled={saving} data-testid="save-dependent">
          {saving ? "Saving..." : isEdit ? "Save changes" : "Add member"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function formatDob(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  } catch (e) {
    return "";
  }
}
